"use client"

import * as React from "react"

import { Input } from "@/components/ui/input"

/**
 * 24-hour clock field ("HH:MM").
 *
 * A native `<input type="time">` renders in the browser's UI language, not
 * the app's — Chrome set to English (US) shows an AM/PM picker and no
 * attribute can force it to 24h. This is a plain text input that masks
 * keystrokes into HH:MM, so every clinic sees the same 24-hour clock the API
 * validates (`date_format:H:i`). The emitted value is identical to what the
 * native control would have produced, so nothing downstream changes.
 *
 * Controlled: `value` is the current "HH:MM" (or a partial draft while
 * typing), `onChange` receives the masked draft on every keystroke and the
 * normalised "HH:MM" on blur ("9" → "09:00", "930" → "09:30"). Values that
 * cannot be a clock time ("29:70") are left as typed so the form's validator
 * can flag them instead of silently rewriting them.
 */

export const TIME_24H_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/

/** Keystroke mask: digits only, the colon appears once the minutes start. */
export function maskTime24(raw: string): string {
  // A colon typed by hand after a 1–2 digit hour is honoured as-is so the
  // caret never fights the user ("1:" → "1:30").
  const explicit = raw.match(/^(\d{1,2}):(\d{0,2})/)
  if (explicit) return `${explicit[1]}:${explicit[2]}`

  const digits = raw.replace(/\D/g, "").slice(0, 4)
  if (digits.length < 2) return digits

  // Hours that can only be single-digit ("9", "25") get their leading zero
  // so "930" lands on 09:30 rather than the impossible 93:0.
  const twoDigitHour = Number(digits.slice(0, 2)) <= 23
  const hours = twoDigitHour ? digits.slice(0, 2) : `0${digits[0]}`
  const minutes = twoDigitHour ? digits.slice(2, 4) : digits.slice(1, 3)

  if (!minutes && twoDigitHour) return hours
  return `${hours}:${minutes}`
}

/**
 * Blur normalisation. Returns "" for blank, a padded "HH:MM" for anything
 * that parses as a clock time, and null when the draft is not a valid time.
 */
export function normalizeTime24(raw: string): string | null {
  const trimmed = raw.trim()
  if (!trimmed) return ""

  let hours: string
  let minutes: string
  if (trimmed.includes(":")) {
    const [h, m = ""] = trimmed.split(":")
    hours = h
    minutes = m
  } else {
    const digits = trimmed.replace(/\D/g, "")
    hours = digits.length <= 2 ? digits : digits.slice(0, -2)
    minutes = digits.length <= 2 ? "0" : digits.slice(-2)
  }

  if (!/^\d{1,2}$/.test(hours) || !/^\d{0,2}$/.test(minutes)) return null
  const h = Number(hours)
  const m = Number(minutes || "0")
  if (h > 23 || m > 59) return null
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
}

/**
 * The soft variants, for fields that are a clock time MOST of the time but
 * legitimately free text (ArtDent 260924: the hotel's own check-in/out
 * wording — "from 13:00" usually, "after 14:00, on request" sometimes).
 * Digits (and a colon) get the flight-field treatment — "1430" → "14:30",
 * "930" → "09:30" — and the moment a letter or space appears the value is
 * left exactly as typed.
 */
export function softMaskTime24(raw: string): string {
  return /^[\d:]*$/.test(raw) ? maskTime24(raw) : raw
}

export function softNormalizeTime24(raw: string): string {
  if (!/^[\d:]+$/.test(raw.trim())) return raw
  return normalizeTime24(raw) ?? raw
}

type TimeInputProps = Omit<
  React.ComponentProps<typeof Input>,
  "type" | "value" | "onChange" | "inputMode" | "maxLength" | "pattern"
> & {
  value: string | undefined
  onChange: (value: string) => void
}

/** Free-text field with the soft time mask above. Caller sets maxLength/placeholder. */
export function TimeOrTextInput({
  value,
  onChange,
  onBlur,
  ...props
}: Omit<React.ComponentProps<typeof Input>, "type" | "value" | "onChange"> & {
  value: string | undefined
  onChange: (value: string) => void
}) {
  return (
    <Input
      type="text"
      autoComplete="off"
      value={value ?? ""}
      onChange={(event) => onChange(softMaskTime24(event.target.value))}
      onBlur={(event) => {
        const normalized = softNormalizeTime24(event.target.value)
        if (normalized !== event.target.value) {
          onChange(normalized)
        }
        onBlur?.(event)
      }}
      {...props}
    />
  )
}

export function TimeInput({ value, onChange, onBlur, ...props }: TimeInputProps) {
  return (
    <Input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      maxLength={5}
      placeholder="HH:MM"
      value={value ?? ""}
      onChange={(event) => onChange(maskTime24(event.target.value))}
      onBlur={(event) => {
        const normalized = normalizeTime24(event.target.value)
        if (normalized !== null && normalized !== event.target.value) {
          onChange(normalized)
        }
        onBlur?.(event)
      }}
      {...props}
    />
  )
}
